import anime from 'animejs';
import Component from '../classes/Component';

const EASE_OUT = 'cubicBezier(0,0,.58,1)';

export default class Hero extends Component {
	constructor() {
		super({
			element: '.c-hero',
			elements: {
				initialSequence: '.c-sequence--start',
				initialSequenceItems: '.c-sequence--start .c-sequence__transaction',
				initialSequenceButton: '.c-sequence--start .c-mockup__button',
				endingSequence: '.c-sequence--end',
				initialScreen: '[data-screen-initial]',
				initialScreenLines: '[data-screen-initial] .c-mockup__line',
				firstScreen: '[data-screen-1]',
				secondScreen: '[data-screen-2]',
				thirdScreen: '[data-screen-3]',
				fourthScreen: '[data-screen-4]',
				pricelist: '.c-pricelist',
				priceDesc: '.c-pricelist__description',
				itemsPrice: '.c-pricelist__price',
				firstScreenTotalPrice: '[data-screen-1] .c-total',
				secondScreenTotalPrice: '[data-screen-2] .c-total',
				thirdScreenTotalPrice: '[data-screen-3] .c-total',
				firstScreenButton: '[data-screen-1] .c-mockup__button',
				secondScreenButton: '[data-screen-2] .c-mockup__button',
				secondScreenButtonTranslate: '[data-screen-2] [data-button-translate]',
				secondScreenButtonlast: '[data-screen-2] [data-button-last]',
				thirdScreenKeypads: '[data-screen-3] .c-keypad',
				thirdScreenKeypadsEnter: '[data-screen-3] .c-keypad--enter',
				thirdScreenKeypadClear: '[data-screen-3] .c-keypad--clear',
				fourthSuccess: '[data-screen-4] .c-success__title',
				pins: '.c-pins',
				pinMask: '.c-pins__mask',
				codeMethods: '[data-method]',
				codeInitiated: '[data-initiated]',
				key5: '[data-key="5"]',
				key6: '[data-key="6"]',
				key8: '[data-key="8"]',
				keyEnter: '[data-key="enter"]',
				mockup: '.c-mockup',
				replaySection: '[data-replay]',
				replayButton: '[data-replay] button',
				heroPoster: '[data-hero-poster]',
			},
		});

		this.firstTl = anime.timeline({ autoplay: false });
		this.secondTl = null;
		this.thirdTl = null;
		this.fourthTl = null;
		this.isInView = false;
		this.totalPriceTranslateValue = 16;

		this.setFirstTimeline();
		this.setSecondTimeline();
		this.setThirdTimeline();
		this.setFourthTimeline();
		this.addEventListeners();

		this.createObserver();
	}

	// eslint-disable-next-line class-methods-use-this
	activateCodeElement(el) {
		el.classList.add('active');
		el.parentElement.classList.add('active');
		el.textContent = 'true';
		setTimeout(() => {
			el.classList.remove('active');
			el.parentElement.classList.remove('active');
		}, 400);
	}

	// eslint-disable-next-line class-methods-use-this
	deactivateCodeElement(el) {
		el.textContent = 'false';
	}

	heroAnimation() {
		// prettier-ignore
		const {
			initialSequence,
			initialSequenceItems,
			initialSequenceButton,
			initialScreen,
			initialScreenLines,
			firstScreen,
			mockup,
			replaySection,
			heroPoster,
		} = this.elements;

		replaySection.classList.add('hide');

		anime({
			targets: [initialSequence, initialScreen],
			opacity: [0, 1],
			begin: () => {
				heroPoster.classList.add('hidden');
			},
		});

		anime({
			targets: initialScreenLines,
			opacity: [0, 1],
			scaleX: [0.5, 1],
			duration: 375,
			easing: EASE_OUT,
			delay: 200,
		});

		const reversedLineAnimation = anime({
			targets: initialScreenLines,
			autoplay: false,
			opacity: [1, 0],
			scaleX: [1, 1],
			duration: 375,
			easing: EASE_OUT,
		});

		const reversedSequenceAnimation = anime({
			targets: initialSequenceItems,
			autoplay: false,
			opacity: [1, 0],
			translateX: [0, -16],
			duration: 375,
			easing: EASE_OUT,
			delay: anime.stagger(125),
		});

		const reversedButtonAnimation = anime({
			targets: initialSequenceButton,
			opacity: [1, 0],
			translateY: [0, 32],
			duration: 375,
			easing: EASE_OUT,
		});

		anime({
			targets: initialSequenceButton,
			opacity: [0, 1],
			translateY: [32, 0],
			duration: 375,
			easing: EASE_OUT,
			delay: 750,
		});

		anime({
			targets: initialSequenceItems,
			opacity: [0, 1],
			translateX: [-16, 0],
			duration: 375,
			easing: EASE_OUT,
			delay: anime.stagger(350),
			complete: () => {
				setTimeout(() => {
					initialSequenceButton.classList.add('pressed');
				}, 750);

				setTimeout(() => {
					reversedLineAnimation.play();
					reversedSequenceAnimation.play();
					reversedButtonAnimation.play();
					firstScreen.classList.add('active');
					this.firstTl.restart();
					mockup.classList.remove('translate');
					heroPoster.classList.remove('hidden');
				}, 1000);
			},
		});
	}

	setFirstTimeline() {
		const {
			pricelist, priceDesc, itemsPrice, firstScreenTotalPrice, firstScreenButton,
		} = this.elements;

		this.firstTl.add({
			targets: pricelist,
			delay: 200,
			scale: [0.8, 1],
			opacity: [0, 1],
			duration: 800,
			easing: EASE_OUT,
		});

		this.firstTl.add({
			targets: priceDesc,
			opacity: [0, 1],
			scaleX: [0, 1],
			duration: 500,
			easing: EASE_OUT,
			delay: anime.stagger(120),
		});

		this.firstTl.add(
			{
				targets: itemsPrice,
				opacity: [0, 1],
				duration: 300,
				easing: 'linear',
				delay: anime.stagger(100),
			},
			'-=500',
		);

		this.firstTl.add(
			{
				targets: [firstScreenTotalPrice, firstScreenButton],
				opacity: [0, 1],
				translateY: [this.totalPriceTranslateValue, 0],
				duration: 800,
				easing: EASE_OUT,
				delay: anime.stagger(120),
				complete: () => {
					setTimeout(() => {
						firstScreenButton.classList.add('pressed');
					}, 750);

					setTimeout(() => {
						this.secondTl.restart();
					}, 1000);
				},
			},
			'-=500',
		);
	}

	setSecondTimeline() {
		const {
			firstScreen, secondScreen, pricelist, firstScreenTotalPrice, secondScreenTotalPrice, firstScreenButton, secondScreenButton,
		} = this.elements;

		const y1 = firstScreenTotalPrice.getBoundingClientRect().top;
		const y2 = secondScreenTotalPrice.getBoundingClientRect().top;

		if (!this.secondTl) {
			this.secondTl = anime.timeline({
				autoplay: false,
			});

			this.secondTl.add({
				targets: pricelist,
				opacity: [1, 0],
				duration: 250,
				easing: EASE_OUT,
			});

			this.secondTl.add(
				{
					targets: firstScreenButton,
					opacity: [1, 0],
					translateY: [0, 16],
					duration: 500,
					easing: EASE_OUT,
				},
				'-=250',
			);

			this.secondTl.add(
				{
					targets: firstScreenTotalPrice,
					translateY: [0, `${y2 - y1 + this.totalPriceTranslateValue}px`],
					duration: 500,
					easing: EASE_OUT,
					complete: () => {
						firstScreen.classList.remove('active');
						secondScreen.classList.add('active');
						secondScreenTotalPrice.style.opacity = 1;
					},
				},
				'-=300',
			);

			this.secondTl.add({
				targets: secondScreenButton,
				opacity: [0, 1],
				translateY: [16, 0],
				scale: [0.95, 1],
				duration: 750,
				easing: 'easeOutElastic',
				delay: anime.stagger(200),
				complete: () => {
					setTimeout(() => {
						secondScreenButton[0].classList.add('pressed');
					}, 750);

					setTimeout(() => {
						this.thirdTl.restart();
					}, 1000);
				},
			});
		}
	}

	setThirdTimeline() {
		const {
			secondScreen,
			thirdScreen,
			secondScreenTotalPrice,
			thirdScreenTotalPrice,
			secondScreenButton,
			thirdScreenKeypads,
			key5,
			key6,
			key8,
			pinMask,
			pins,
		} = this.elements;

		if (!this.thirdTl) {
			this.thirdTl = anime.timeline({
				autoplay: false,
			});

			this.thirdTl.add({
				targets: secondScreenButton,
				opacity: [1, 0],
				duration: 500,
				easing: EASE_OUT,
				complete: () => {
					secondScreen.classList.remove('active');
					thirdScreen.classList.add('active');
					thirdScreen.style.opacity = 1;
					thirdScreenTotalPrice.style.opacity = 1;
					secondScreenTotalPrice.style.opacity = 0;
					pins.style.opacity = 1;
				},
			});

			this.thirdTl.add({
				targets: thirdScreenKeypads,
				opacity: [0, 1],
				translateY: [10, 0],
				translateX: [10, 0],
				duration: 600,
				easing: EASE_OUT,
				delay: anime.stagger(30, { from: 'center' }),
				complete: () => {
					// pinMask.style.width = `${100 - this.pinCount * 25}%`;

					const starting = 750;
					const delay = 500;

					setTimeout(() => {
						pinMask.style.width = `${100 - 1 * 25}%`;
						key6.classList.add('pressed');
					}, starting);

					setTimeout(() => {
						pinMask.style.width = `${100 - 2 * 25}%`;
						key5.classList.add('pressed');
					}, starting + delay);

					setTimeout(() => {
						pinMask.style.width = `${100 - 3 * 25}%`;
						key8.classList.add('pressed');
					}, starting + delay * 2);

					setTimeout(() => {
						key8.classList.remove('pressed');
					}, starting + delay * 2.5);

					setTimeout(() => {
						pinMask.style.width = `${100 - 4 * 25}%`;
						key8.classList.add('pressed');
					}, starting + delay * 3);

					setTimeout(() => {
						this.fourthTl.restart();
					}, starting + delay * 4);
				},
			});
		}
	}

	setFourthTimeline() {
		const {
			thirdScreen, fourthScreen, pinMask, endingSequence, mockup, replaySection, heroPoster,
		} = this.elements;

		if (!this.fourthTl) {
			this.fourthTl = anime.timeline({
				autoplay: false,
			});

			this.fourthTl.add({
				targets: thirdScreen,
				opacity: [1, 0],
				duration: 500,
				easing: EASE_OUT,
			});

			this.fourthTl.add({
				targets: fourthScreen,
				opacity: [0, 1],
				duration: 500,
				easing: EASE_OUT,
				complete: () => {
					heroPoster.classList.add('hidden');
					mockup.classList.add('translate');
				},
			});

			this.fourthTl.add({
				targets: endingSequence,
				opacity: [0, 1],
				translateY: [56, 0],
				duration: 500,
				easing: EASE_OUT,
				delay: 250,
				complete: () => {
					pinMask.style.width = '100%';
					thirdScreen.classList.remove('active');
					replaySection.classList.remove('hide');

					const allPressed = this.element.querySelectorAll('.pressed');
					allPressed.forEach(el => {
						el.classList.remove('pressed');
					});
				},
			});
		}
	}

	createObserver() {
		const { initialSequence } = this.elements;

		this.observer = new window.IntersectionObserver(entries => {
			entries.forEach(entry => {
				if (!this.isInView && entry.isIntersecting) {
					this.heroAnimation();
					this.isInView = true;
				}
			});
		}).observe(initialSequence);
	}

	resetAnimation() {
		const {
			initialSequence, endingSequence, fourthScreen, heroPoster,
		} = this.elements;

		initialSequence.style.opacity = 0;
		endingSequence.style.opacity = 0;
		fourthScreen.style.opacity = 0;
		heroPoster.classList.remove('hidden');

		this.heroAnimation();
	}

	addEventListeners() {
		const { replayButton } = this.elements;

		replayButton.addEventListener('click', () => {
			this.resetAnimation();
		});
	}
}
